// src/services/codebookApi.ts
import axios from 'axios';
import {
  CodebookGenerateRequest,
  CodebookGenerateResponse,
  CodebookGetResponse,
  CodebookListResponse,
  CodebookShareResponse
} from '../types/chat';
import { tokenManager } from './api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authorization header to requests
api.interceptors.request.use((config) => {
  const token = tokenManager.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      tokenManager.removeToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const codebookApi = {
  /**
   * Generate an interactive programming tutorial codebook.
   *
   * Sends a natural language prompt to the CodebookAgent which generates
   * structured content with narrative sections and executable code examples.
   *
   * Returns:
   *   codebook_id: Unique identifier for tracking this codebook
   *   status: Initial status (generating)
   *   title: Generated title for the tutorial
   *   blocks: Initial empty blocks array (will be populated)
   */
  generateCodebook: async (
    request: CodebookGenerateRequest
  ): Promise<CodebookGenerateResponse> => {
    console.log('[CodebookAPI] POST /api/codebooks/generate');
    console.log('[CodebookAPI] Request prompt:', request.prompt.substring(0, 100));

    try {
      const response = await api.post('/api/codebooks/generate', request);
      console.log('[CodebookAPI] ✅ Codebook generation started:', {
        codebook_id: response.data.codebook_id,
        status: response.data.status,
        title: response.data.title
      });
      return response.data;
    } catch (error: any) {
      console.error('[CodebookAPI] ❌ Codebook generation failed:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message
      });
      throw error;
    }
  },

  /**
   * Retrieve a codebook by ID.
   *
   * Returns:
   *   Complete codebook data including title, prompt, and all content blocks
   */
  getCodebook: async (
    codebook_id: string
  ): Promise<CodebookGetResponse> => {
    console.log('[CodebookAPI] GET /api/codebooks/' + codebook_id);

    try {
      const response = await api.get(`/api/codebooks/${codebook_id}`);
      console.log('[CodebookAPI] ✅ Codebook retrieved:', {
        codebook_id: response.data.codebook_id,
        status: response.data.status,
        blocks: response.data.blocks.length
      });
      return response.data;
    } catch (error: any) {
      console.error('[CodebookAPI] ❌ Get codebook failed:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message
      });
      throw error;
    }
  },

  /**
   * List user's codebooks with pagination.
   *
   * Args:
   *   limit: Number of codebooks per page (default 10)
   *   offset: Pagination offset (default 0)
   *
   * Returns:
   *   List of codebooks with metadata and pagination info
   */
  listCodebooks: async (
    limit: number = 10,
    offset: number = 0
  ): Promise<CodebookListResponse> => {
    console.log('[CodebookAPI] GET /api/codebooks');

    try {
      const response = await api.get('/api/codebooks', {
        params: { limit, offset }
      });
      console.log('[CodebookAPI] ✅ Codebooks listed:', {
        total: response.data.total,
        count: response.data.codebooks.length
      });
      return response.data;
    } catch (error: any) {
      console.error('[CodebookAPI] ❌ List codebooks failed:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message
      });
      throw error;
    }
  },

  /**
   * Download codebook as PDF.
   *
   * Returns:
   *   PDF file blob
   */
  downloadPDF: async (
    codebook_id: string
  ): Promise<Blob> => {
    console.log('[CodebookAPI] GET /api/codebooks/' + codebook_id + '/pdf');

    try {
      const response = await api.get(`/api/codebooks/${codebook_id}/pdf`, {
        responseType: 'blob'
      });
      console.log('[CodebookAPI] ✅ PDF generated:', {
        codebook_id: codebook_id,
        size: response.data.size
      });
      return response.data;
    } catch (error: any) {
      console.error('[CodebookAPI] ❌ PDF download failed:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message
      });
      throw error;
    }
  },

  /**
   * Delete a codebook.
   *
   * Permanently deletes the codebook (ownership verified).
   */
  deleteCodebook: async (
    codebook_id: string
  ): Promise<{ success: boolean; message: string }> => {
    console.log('[CodebookAPI] DELETE /api/codebooks/' + codebook_id);

    try {
      const response = await api.delete(`/api/codebooks/${codebook_id}`);
      console.log('[CodebookAPI] ✅ Codebook deleted:', {
        codebook_id: codebook_id
      });
      return response.data;
    } catch (error: any) {
      console.error('[CodebookAPI] ❌ Delete failed:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message
      });
      throw error;
    }
  },

  /**
   * Get a public shared codebook (no auth required).
   */
  getPublicCodebook: async (
    codebook_id: string
  ): Promise<CodebookGetResponse> => {
    console.log('[CodebookAPI] GET /api/codebooks/shared/' + codebook_id);

    try {
      const response = await api.get(`/api/codebooks/shared/${codebook_id}`);
      console.log('[CodebookAPI] ✅ Public codebook retrieved:', {
        codebook_id: response.data.codebook_id,
        title: response.data.title
      });
      return response.data;
    } catch (error: any) {
      console.error('[CodebookAPI] ❌ Get public codebook failed:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message
      });
      throw error;
    }
  },

  /**
   * Toggle public sharing for a codebook.
   */
  toggleShare: async (
    codebook_id: string,
    is_public: boolean
  ): Promise<CodebookShareResponse> => {
    console.log('[CodebookAPI] PUT /api/codebooks/' + codebook_id + '/share', { is_public });

    try {
      const response = await api.put(`/api/codebooks/${codebook_id}/share`, { is_public });
      console.log('[CodebookAPI] ✅ Share toggled:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[CodebookAPI] ❌ Toggle share failed:', {
        status: error.response?.status,
        message: error.response?.data?.detail || error.message
      });
      throw error;
    }
  }
};

export default codebookApi;
